import React, { useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import AssetIcon from "./AssetIcon";

export default function FileUpload({ files, onChange, testId = "file-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => inputRef.current?.click();

  const handleFiles = async (picked) => {
    if (!picked || picked.length === 0) return;
    setUploading(true);
    const added = [];
    for (const f of picked) {
      try {
        const form = new FormData();
        form.append("file", f);
        const res = await api.post("/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(res.data);
      } catch (e) {
        toast.error(
          `Gagal upload ${f.name}: ${formatApiErrorDetail(e.response?.data?.detail)}`
        );
      }
    }
    onChange([...(files || []), ...added]);
    setUploading(false);
    if (added.length) toast.success(`${added.length} file berhasil diunggah`);
  };

  const remove = (id) => onChange(files.filter((f) => f.id !== id));

  return (
    <div data-testid={testId}>
      <button
        type="button"
        onClick={handlePick}
        data-testid={`${testId}-trigger`}
        className="w-full border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center gap-2 hover:border-[#0B2B5E] hover:bg-slate-50 transition-colors"
      >
        {uploading ? (
          <Loader2 className="h-6 w-6 text-[#0B2B5E] animate-spin" />
        ) : (
          <AssetIcon src="/img/icons/CloudArrowUp.svg" alt="" className="h-8 w-8" tone="muted" />
        )}
        <div className="text-sm font-semibold text-slate-700">
          {uploading ? "Mengunggah..." : "Klik untuk unggah bukti"}
        </div>
        <div className="text-xs text-slate-500">
          JPG, PNG, WEBP, PDF — maks 10MB per file
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        className="hidden"
        onChange={(e) => {
          handleFiles(Array.from(e.target.files || []));
          e.target.value = "";
        }}
        data-testid={`${testId}-input`}
      />
      {files?.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
              data-testid={`uploaded-file-${f.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <AssetIcon
                  src="/img/icons/doc.svg"
                  alt=""
                  className="h-4 w-4 flex-shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-sm text-slate-800 truncate">{f.original_filename}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                    <AssetIcon src="/img/icons/CheckCircle.svg" alt="" className="h-3.5 w-3.5" tone="emerald" />
                    {(f.size / 1024).toFixed(0)} KB
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(f.id)}
                className="text-slate-400 hover:text-red-500"
                data-testid={`remove-file-${f.id}`}
                aria-label="Hapus file"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
